import { Fragment, type ReactNode } from 'react';

import { Form, NavLink, useParams } from 'react-router';

import type { FieldName } from '@conform-to/react';
import { FormProvider, getFormProps, getInputProps, useField, useForm } from '@conform-to/react';
import { parseWithZod } from '@conform-to/zod';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

import type { AttributeFilterFragment } from '@boc/framework/saleor-graphql';

import type { InitialSearchState } from '~/framework/routes/products/search/loader';
import { buttonVariants } from '~/local/components/button';
import { cn } from '~/styling';
import { urls } from '~/urls';

import type { FILTER_ATTRIBUTE_TYPES } from '#/products/search';
import { DEFAULT_FILTER_ATTRIBUTES } from '#/products/search';

import { schemaSearch } from '../schema';

export const MobileSidebar = ({
  heading,
  show,
  setShow,
  children,
}: {
  heading: string;
  show: boolean;
  setShow: (show: boolean) => void;
  children: ReactNode;
}) => {
  return (
    <Transition.Root show={show} as={Fragment}>
      <Dialog as="div" className="relative z-40 lg:hidden" onClose={setShow}>
        <Transition.Child
          as={Fragment}
          enter="transition-opacity ease-linear duration-300"
          enterFrom="opacity-100"
          enterTo="opacity-0"
          leave="transition-opacity ease-linear duration-300"
          leaveFrom="opacity-0"
          leaveTo="opacity-100"
        >
          <div className="bg-black fixed inset-0 bg-opacity-25" />
        </Transition.Child>
        <div className="fixed inset-0 z-40 flex">
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-1200 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <Dialog.Panel className="relative flex h-full w-full flex-col overflow-y-auto bg-primary-2-10 p-10">
              <div className="space-y-3 text-primary-2-80">
                <div className="flex items-center justify-between">
                  <h2 className="font-heading-condensed text-lg font-medium leading-6 tracking-tight">
                    {heading}
                  </h2>
                  <button
                    type="button"
                    className="flex h-10 w-10 items-center justify-center p-2"
                    onClick={() => setShow(false)}
                  >
                    <span className="sr-only">Close menu</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div className="w-full border-b border-primary-2-20" />
                {children}
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

const ChoiceFilter = ({
  name,
  choices,
}: {
  name: FieldName<{ values: string | string[] }>;
  choices: { label?: string; value: string }[];
}) => {
  const [field] = useField(`${name}.values`);

  return (
    <div className="space-y-2">
      {choices.map((choice) => {
        return (
          <label key={choice.value} className="flex items-center">
            <input {...getInputProps(field, { type: 'checkbox', value: choice.value })} />
            <span className="ml-2">{choice.label || choice.value}</span>
          </label>
        );
      })}
    </div>
  );
};

const RangeFilter = ({ name }: { name: FieldName<{ gte?: number; lte?: number }> }) => {
  const [gteField] = useField(`${name}.gte`);
  const [lteField] = useField(`${name}.lte`);

  return (
    <div className="space-y-2">
      <input
        className=""
        placeholder="Min"
        {...getInputProps(gteField, { type: 'number' })}
      />
      <div>to</div>
      <input
        className=""
        placeholder="Max"
        {...getInputProps(lteField, { type: 'number' })}
      />
    </div>
  );
};

const FilterSection = ({ heading, children }: { heading: string; children: ReactNode }) => {
  return (
    <div className="">
      <div className="font-heading-condensed text-lg font-medium leading-6 tracking-tight">
        {heading}
      </div>
      <div className="mt-4">{children}</div>
    </div>
  );
};

const AttributeFilter = ({
  attribute,
}: {
  attribute: AttributeFilterFragment & {
    filterConfig: { widget: FILTER_ATTRIBUTE_TYPES };
    filterField: { name: FieldName<{ values: string | string[] }> };
  };
}) => {
  switch (attribute.filterConfig.widget) {
    case 'checkboxes':
      return attribute.choices ? (
        <ChoiceFilter
          name={attribute.filterField.name}
          choices={attribute.choices.edges.map((choice) => ({
            value: choice.node.slug || '',
            label: choice.node.name || '',
          }))}
        />
      ) : null;
  }
};

const AttributeFilterSection = ({
  attribute,
}: {
  attribute: AttributeFilterFragment & {
    filterConfig: { widget: FILTER_ATTRIBUTE_TYPES };
    filterField: { name: FieldName<{ values: string | string[] }> };
  };
}) => {
  return (
    <FilterSection key={attribute.slug} heading={attribute.name || ''}>
      <AttributeFilter attribute={attribute} />
    </FilterSection>
  );
};

export const FilterForm = ({
  initialSearchState,
  categories,
  attributes,
  setShowFilters,
}: {
  categories?: { id: string; name: string; slug: string }[];
  attributes?: AttributeFilterFragment[];
  initialSearchState?: InitialSearchState;
  setShowFilters?: (show: boolean) => void;
}) => {
  const params = useParams();

  const [form, fields] = useForm({
    defaultValue: initialSearchState,
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: schemaSearch });
    },
  });

  const attributeFields = fields.attributes.getFieldset();

  const cleanedAttributes = attributes
    ?.filter((attribute) => attribute.slug && DEFAULT_FILTER_ATTRIBUTES[attribute.slug])
    .map((attribute) => {
      const slug = attribute.slug!;
      return {
        ...attribute,
        slug,
        filterField: attributeFields[slug],
        filterConfig: DEFAULT_FILTER_ATTRIBUTES[slug],
      };
    });

  return (
    <Form method="get" preventScrollReset={true} {...getFormProps(form)}>
      <FormProvider context={form.context}>
        {initialSearchState?.sort && (
          <input type="hidden" name="sort" value={initialSearchState.sort} />
        )}
        {initialSearchState?.direction && (
          <input type="hidden" name="direction" value={initialSearchState.direction} />
        )}
        <div className="flex items-center justify-between">
          <div className="font-heading-condensed text-lg font-medium leading-6 tracking-tight">
            Filters
          </div>
          <button
            type="submit"
            onClick={() => setShowFilters && setShowFilters(false)}
            className={cn(buttonVariants({ size: 'sm' }))}
          >
            Apply
          </button>
        </div>

        {categories ? (
          <div className="mt-6">
            <FilterSection heading={'Category'}>
              {categories?.map((category) => {
                return (
                  <NavLink
                    preventScrollReset={true}
                    key={category.id}
                    to={urls.products.categories(params, { slug: category.slug })}
                    className={({ isActive }) => cn('block', isActive && 'font-bold')}
                  >
                    {category.name}
                  </NavLink>
                );
              })}
            </FilterSection>
          </div>
        ) : null}

        <div className={'mt-6 space-y-6'}>
          <FilterSection heading={'Price'}>
            <RangeFilter name={fields.price.name} />
          </FilterSection>
          {cleanedAttributes?.map((attribute) => {
            return <AttributeFilterSection key={attribute.slug} attribute={attribute} />;
          })}

          <button
            type="submit"
            onClick={() => setShowFilters && setShowFilters(false)}
            className={cn(buttonVariants({ className: 'block w-full' }))}
          >
            Apply Filters
          </button>
        </div>
      </FormProvider>
    </Form>
  );
};
